/* @jsx h */
import { defineComponent, ref, h, onMounted, computed } from "vue";
import { api } from "@app/data/index";
import {
  Service,
  Category,
  Product,
  TableStatus,
  FoodType,
  CartPersona,
} from "@lemmonhq/types";
import {
  Loading,
  CartComponent,
  Catalog,
  BundleModal,
  GiftCardModal,
  type GiftCardSelection,
  POSMobileCartButton,
  NotesModal,
  TableSelectModal,
} from "@app/vue/components/index";
import {
  createSeatPersonas,
  sortCatalogItems,
} from "@app/utils/clientBusiness";
import { useRoute, useRouter } from "vue-router";
import { needsFetchBridge, setTitle, useCart } from "@lemmonhq/orders";

import { AddCustomerModal } from "../components/POSComponent/AddCustomerModal";
import { nanoid } from "nanoid";
import { TableSwitcherModal } from "../components/POSComponent/TableSwitcherModal";
import { isMobileTabBarPrototypeEnabled } from "../components/LayoutComponents/Navbar/mobileTabBarPrototype";

export type POSInternalProps = {
  products: Product[];
  services: Service[];
  tables: TableStatus[];
  foodTypes: FoodType[];
  onTablesUpdated?: () => void;
};

const POSInternal = defineComponent<POSInternalProps>({
  props: ["products", "services", "tables", "foodTypes", "onTablesUpdated"],
  setup(props) {
    // Use the cart composable
    setTitle("POS");
    const cartAPI = useCart();
    const isCheckoutOpen = ref(false);
    const {
      cart,
      addToCart,
      incrementQuantity,
      decrementQuantity,
      removeItem,
      updateNotes,
      updateCart,
    } = cartAPI;
    const notes = ref({});
    const isNotesModalOpen = ref(false);
    const isTableSelectModalOpen = ref(false);

    const selectedService = ref<Service | null>(null);
    const selectedCategory = ref<Category | null>(null);
    const bundleProduct = ref<Product | null>(null);
    const giftCardProduct = ref<Product | null>(null);

    const route = useRoute();
    const selectedTable = computed(() => {
      console.log("selectedTable", route.query);
      const tableId = route.query.tableId as string;
      return tableId
        ? props.tables.find((t) => t.id.toString() === tableId)
        : undefined;
    });

    const handleServiceSelect = (service: Service | null) => {
      selectedService.value = service;
      selectedCategory.value =
        (service?.categories && sortCatalogItems(service?.categories)[0]) ||
        null;
    };

    const handleCategorySelect = (category: Category) => {
      selectedCategory.value = category;
    };

    const handleProductSelect = (product: Product) => {
      if (product.is_gift_card) {
        giftCardProduct.value = product;
        return;
      }
      if (
        (product.bundles && product.bundles.length > 0) ||
        product.is_custom
      ) {
        bundleProduct.value = product;
      } else {
        addToCart(product);
      }
      console.log("handleProductSelect", cart);
    };

    const handleBundleConfirm = (
      selections: { bundleId: number; optionIds: number[] }[],
      customPrice?: number,
    ) => {
      if (!bundleProduct.value) return;

      const formattedBundles = selections.map(({ bundleId, optionIds }) => {
        const bundle = bundleProduct.value!.bundles!.find(
          (b) => b.id === bundleId,
        )!;
        return {
          bundleId,
          name: bundle.name,
          options: optionIds.map((optionId) => {
            const option = bundle.options.find((o) => o.id === optionId)!;
            return {
              id: option.id,
              title: option.display.title,
              entity_id: option.entity_id,
              entity_type: option.entity_type,
              price: option.price,
            };
          }),
        };
      });

      addToCart(bundleProduct.value, formattedBundles, customPrice);
      bundleProduct.value = null;
    };

    const handleGiftCardConfirm = ({
      amount,
      tableId,
      holder,
    }: GiftCardSelection) => {
      const product = giftCardProduct.value;
      if (!product) return;
      addToCart(product, undefined, amount, undefined, undefined, undefined, {
        tableCreditTableId: tableId,
        giftCardHolder: holder,
      });
      giftCardProduct.value = null;
    };

    onMounted(() => {
      setTitle("POS");
    });
    const onClear = () => {
      notes.value = {};
      cartAPI.showFoodTypes.value = false;
      cartAPI.activeSection.value = "cart";
      router.push({
        path: route.path,
        query: {
          ...route.query,
          tableId: undefined,
        },
      });

      updateCart({ ...cart, items: [], customers: [] });
    };
    const router = useRouter();

    return () => {
      return (
        <>
          <div class="pl-0 pr-0 flex flex-col gap-4 flex-1 overflow-auto pt-0 lg:pt-0 pb-0 px-0 sm:flex-row sm:gap-0">
            <div class="hidden lg:flex pl-2 py-4 flex-shrink-0 md:w-[360px] h-full">
              <CartComponent
                cart={cart}
                cartAPI={cartAPI}
                onClear={onClear}
                notes={notes}
                onIncrement={incrementQuantity}
                onDecrement={decrementQuantity}
                onRemove={removeItem}
                onUpdateNotes={updateNotes}
                onUpdateCart={updateCart}
                services={props.services}
                foodTypes={props.foodTypes}
                selectedService={selectedService.value}
                selectedCategory={selectedCategory.value}
                onServiceSelect={handleServiceSelect}
                onCategorySelect={handleCategorySelect}
                onProductSelect={handleProductSelect}
                products={props.products.filter(
                  (el) => el.status == "available",
                )}
                selectedTable={selectedTable}
                isNotesModalOpen={isNotesModalOpen}
                isTableSelectModalOpen={isTableSelectModalOpen}
              />
            </div>
            <div class="flex-col flex-1 overflow-hidden relative flex">
              <Catalog
                services={props.services}
                selectedService={selectedService.value}
                selectedCategory={selectedCategory.value}
                onServiceSelect={handleServiceSelect}
                onCategorySelect={handleCategorySelect}
                onProductSelect={handleProductSelect}
                products={props.products.filter(
                  (el) => el.status == "available",
                )}
                selectedTable={selectedTable}
                cartItems={cart.items}
                cartAPI={cartAPI}
                onTableUpdated={props.onTablesUpdated}
              />
            </div>
            {bundleProduct.value && (
              <BundleModal
                product={bundleProduct.value}
                bundles={bundleProduct.value.bundles || []}
                isOpen={true}
                onClose={() => (bundleProduct.value = null)}
                onConfirm={handleBundleConfirm}
              />
            )}
            {giftCardProduct.value && (
              <GiftCardModal
                product={giftCardProduct.value}
                defaultTableId={selectedTable.value?.id ?? null}
                onClose={() => (giftCardProduct.value = null)}
                onConfirm={handleGiftCardConfirm}
              />
            )}
          </div>
          {isNotesModalOpen.value && (
            <NotesModal
              notes={notes}
              items={cart.items}
              isOpen={isNotesModalOpen.value}
              onClose={() => (isNotesModalOpen.value = false)}
              onSave={updateNotes}
            />
          )}
          {cartAPI.isAddSeatsModalOpen.value && (
            <AddCustomerModal
              onClose={() => (cartAPI.isAddSeatsModalOpen.value = false)}
              onConfirm={(count) => {
                const { customers, firstCustomerId } = createSeatPersonas({
                  count,
                  existingCount: cartAPI.cart.customers.length,
                  createId: () => nanoid(),
                });
                let lastCustomerId = firstCustomerId;
                cartAPI.cart.customers.push(...customers);
                //                cartAPI.activeCustomerId.value = lastCustomerId;
                cartAPI.isAddSeatsModalOpen.value = false;
              }}
            />
          )}
          {cartAPI.isTableSwitcherOpen.value ? (
            <TableSwitcherModal
              cart={cartAPI}
              tables={props.tables}
              onClose={() => {
                cartAPI.isTableSwitcherOpen.value = false;
              }}
              onConfirm={(tableId, pay) => {
                router.push({
                  path: route.path,
                  query: {
                    ...route.query,
                    tableId: String(tableId),
                  },
                });
                cartAPI.isTableSwitcherOpen.value = false;
                if (pay) {
                  cartAPI.activeSection.value = "table";
                }
              }}
            />
          ) : null}

          {isTableSelectModalOpen.value && (
            <TableSelectModal
              foodTypes={props.foodTypes}
              cart={cart}
              selectedTable={selectedTable.value}
              isOpen={isTableSelectModalOpen.value}
              onClose={() => (isTableSelectModalOpen.value = false)}
              cartAPI={cartAPI}
              onSuccess={() => {
                onClear();
                isTableSelectModalOpen.value = false;
                isCheckoutOpen.value = false;
                router.push("/app/pos");
              }}
              products={props.products}
            />
          )}
          <div
            class={[
              `flex lg:hidden items-center justify-center absolute left-4 right-4 overflow-hidden`,
              needsFetchBridge ? "bottom-24" : "bottom-20",
            ]}
          >
            <POSMobileCartButton
              cart={cart}
              cartAPI={cartAPI}
              onClear={onClear}
              isNotesModalOpen={isNotesModalOpen}
              isTableSelectModalOpen={isTableSelectModalOpen}
              isCheckoutOpen={isCheckoutOpen}
              selectedTable={selectedTable.value}
              products={props.products}
              foodTypes={props.foodTypes}
              customers={cartAPI.cart.customers}
            />
          </div>
        </>
      );
    };
  },
});

export const POSPage = defineComponent({
  setup() {
    const loaded = ref(false);
    const products = ref<Product[]>([]);
    const services = ref<Service[]>([]);
    const tables = ref<TableStatus[]>([]);
    const foodTypes = ref<FoodType[]>([]);

    const fetchData = async () => {
      const [
        fetchedProducts,
        fetchedServices,
        fetchedTables,
        fetchedFoodTypes,
      ] = await Promise.all([
        api.getProducts(),
        api.getServices(),
        api.getTablesMeta(),
        api.getFoodTypes(),
      ]);

      products.value = fetchedProducts.filter(
        (el) => el.status === "available" && !el.deleted_at,
      );
      services.value = fetchedServices;
      tables.value = fetchedTables;
      foodTypes.value = fetchedFoodTypes;
      loaded.value = true;
    };

    onMounted(() => {
      fetchData();
    });

    return () => {
      return loaded.value ? (
        <POSInternal
          products={products.value}
          services={services.value}
          tables={tables.value}
          foodTypes={foodTypes.value}
          onTablesUpdated={fetchData}
        />
      ) : (
        <div class="w-full h-full flex-1 flex justify-center items-center p-8">
          <Loading />
        </div>
      );
    };
  },
});
